import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callAnalysisSettingsAPI, customBusinessModelAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { Card, Spinner, Input, Button } from "../../components/UI";
import {
  HiOutlinePhone,
  HiOutlineSparkles,
  HiOutlineCursorClick,
  HiOutlineRefresh,
  HiOutlineLockClosed,
  HiOutlineOfficeBuilding,
  HiOutlineHome,
  HiOutlineShieldCheck,
  HiOutlineAcademicCap,
  HiOutlineShoppingCart,
  HiOutlineHeart,
  HiOutlineGlobe,
  HiOutlineTruck,
  HiOutlineCloud,
  HiOutlineBriefcase,
  HiOutlineCheckCircle,
  HiOutlineKey,
  HiOutlineExclamationCircle,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlinePlusCircle,
} from "react-icons/hi";

// Two call analysis modes for Settings → Calls:
// - manual: admins pick the specific calls to analyze themselves
// - auto:   recordings are analyzed directly (automatically), limited per day
//           — the daily limit is set by the SUPERADMIN per organization
const MODE_OPTIONS = [
  {
    value: "manual",
    label: "Manual Analysis",
    description:
      "Select the specific calls you want analyzed. Go to Call Analysis (or Recordings), press \"Call Analysis\" on a recording and the AI transcript + coaching insights are generated for that call only.",
    icon: HiOutlineCursorClick,
    color: "text-blue-600 bg-blue-50 border-blue-200",
    activeColor: "bg-blue-600 text-white border-blue-600",
  },
  {
    value: "auto",
    label: "Direct (Automatic) Analysis",
    description:
      "Every call recording is analyzed directly and automatically as soon as it arrives — no manual selection needed. Limited per day by your plan (limit is set by the Superadmin).",
    icon: HiOutlineRefresh,
    color: "text-purple-600 bg-purple-50 border-purple-200",
    activeColor: "bg-purple-600 text-white border-purple-600",
  },
];

// Icons for each business model (key → icon). Labels/descriptions come from
// the server (single source of truth in services/ai/config.js).
const BUSINESS_MODEL_ICONS = {
  general: HiOutlineBriefcase,
  insurance: HiOutlineShieldCheck,
  banking_finance: HiOutlineOfficeBuilding,
  real_estate: HiOutlineHome,
  edtech: HiOutlineAcademicCap,
  ecommerce: HiOutlineShoppingCart,
  healthcare: HiOutlineHeart,
  travel: HiOutlineGlobe,
  automotive: HiOutlineTruck,
  telecom: HiOutlinePhone,
  b2b: HiOutlineCloud,
};

const CallSettings = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["callAnalysisSettings"],
    queryFn: async () => {
      const response = await callAnalysisSettingsAPI.get();
      return response.data?.data || response.data || {};
    },
  });

  const mode = data?.mode || "manual";
  const limit = data?.autoDailyLimit ?? 10;
  const usedToday = data?.autoUsedToday ?? 0;

  // Business model — comes from the server (key, label, description list)
  const businessModel = data?.businessModel || "general";
  const businessModelOptions = (data?.businessModelOptions || []).map((m) => ({
    ...m,
    icon: BUSINESS_MODEL_ICONS[m.key] || HiOutlineBriefcase,
  }));

  const updateMutation = useMutation({
    mutationFn: (newMode) => callAnalysisSettingsAPI.updateMode(newMode),
    onSuccess: () => {
      queryClient.invalidateQueries(["callAnalysisSettings"]);
      toast.success("Call analysis mode updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update call analysis mode");
    },
  });

  const businessModelMutation = useMutation({
    mutationFn: (newModel) => callAnalysisSettingsAPI.updateBusinessModel(newModel),
    onSuccess: (response, newModel) => {
      queryClient.invalidateQueries(["callAnalysisSettings"]);
      const label =
        response.data?.data && businessModelOptions.find((m) => m.key === newModel)?.label;
      toast.success(
        `Business model set to ${label || newModel} — AI will identify and score calls for this business`,
      );
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update business model");
    },
  });

  const handleSelect = (value) => {
    if (value === mode) return;
    updateMutation.mutate(value);
  };

  const handleSelectBusinessModel = (value) => {
    if (value === businessModel || businessModelMutation.isPending) return;
    businessModelMutation.mutate(value);
  };

  // ─── Custom business models (Add Custom) ────────────────────────────────
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState({ label: "", description: "", guidance: "" });
  const [customFormError, setCustomFormError] = useState("");

  const createCustomMutation = useMutation({
    mutationFn: (data) => customBusinessModelAPI.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["callAnalysisSettings"]);
      toast.success(response.data?.message || "Custom business model added");
      setShowCustomForm(false);
      setCustomForm({ label: "", description: "", guidance: "" });
      setCustomFormError("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add custom business model");
    },
  });

  const deleteCustomMutation = useMutation({
    mutationFn: (key) => customBusinessModelAPI.remove(key),
    onSuccess: () => {
      queryClient.invalidateQueries(["callAnalysisSettings"]);
      toast.success("Custom business model removed");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to remove custom business model");
    },
  });

  const handleCreateCustom = (e) => {
    e.preventDefault();
    setCustomFormError("");
    if (!customForm.label.trim()) {
      setCustomFormError("Please enter a name for the business model");
      return;
    }
    createCustomMutation.mutate({
      label: customForm.label.trim(),
      description: customForm.description.trim(),
      guidance: customForm.guidance.trim(),
    });
  };

  const handleDeleteCustom = (option) => {
    if (
      window.confirm(
        `Remove the custom business model "${option.label}"?` +
          (businessModel === option.key
            ? " It is currently selected — the organization will fall back to General."
            : ""),
      )
    ) {
      deleteCustomMutation.mutate(option.key);
    }
  };

  // ─── Transcription (Deepgram) API key — powers AI Call Analysis ─────────
  // When the old key expires, a super admin pastes a new one here; the server
  // validates it against Deepgram, applies it immediately (call analysis
  // continues without any error) and persists it across restarts.
  const { user } = useAuth();
  // Admins (admin / super_admin) can add, replace or remove the key any time;
  // managers only see the status.
  const canManageKey = user?.role === "admin" || user?.role === "super_admin";

  const [apiKeyInput, setApiKeyInput] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);

  const apiKeyQuery = useQuery({
    queryKey: ["callAnalysisApiKey"],
    queryFn: async () => {
      const response = await callAnalysisSettingsAPI.getApiKeyStatus();
      return response.data?.data || {};
    },
    // Every admin can see whether transcription is active; only super admins
    // get the masked key details and can change the key.
  });
  const keyStatus = apiKeyQuery.data || {};

  const updateApiKeyMutation = useMutation({
    mutationFn: (apiKey) => callAnalysisSettingsAPI.updateApiKey(apiKey),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["callAnalysisApiKey"]);
      queryClient.invalidateQueries(["callAnalysisSettings"]);
      queryClient.invalidateQueries(["call-analysis-providers"]);
      toast.success(response.data?.message || "API key updated — call analysis continues with the new key");
      setApiKeyInput("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update API key");
    },
  });

  const clearApiKeyMutation = useMutation({
    mutationFn: () => callAnalysisSettingsAPI.clearApiKey(),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["callAnalysisApiKey"]);
      queryClient.invalidateQueries(["callAnalysisSettings"]);
      queryClient.invalidateQueries(["call-analysis-providers"]);
      toast.success(response.data?.message || "Custom API key removed — server default restored");
      setApiKeyInput("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to reset API key");
    },
  });

  const handleUpdateApiKey = (e) => {
    e.preventDefault();
    const apiKey = apiKeyInput.trim();
    if (!apiKey) {
      toast.error("Please paste the new API key");
      return;
    }
    updateApiKeyMutation.mutate(apiKey);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <HiOutlinePhone className="w-6 h-6 text-primary-600" />
          Call Analysis Mode
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose how AI call analysis runs for your organization — pick calls manually, or let
          every recording be analyzed directly as it arrives. Also select your Business Model below
          so the AI identifies and scores calls for your type of business.
        </p>
      </div>

      {/* Mode Options */}
      <div className="space-y-3">
        {MODE_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isActive = mode === option.value;

          return (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              disabled={updateMutation.isPending}
              className={`
                w-full text-left p-4 rounded-xl border-2 transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isActive ? option.activeColor + " shadow-md" : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"}
              `}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? "bg-white/20" : option.color.split(" ").slice(0, 2).join(" ")}`}>
                  <Icon className={`w-5 h-5 ${isActive ? "text-white" : option.color.split(" ")[0]}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold text-sm ${isActive ? "text-white" : "text-gray-900"}`}>
                      {option.label}
                    </p>
                    {isActive && (
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-white font-medium">
                        Active
                      </span>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${isActive ? "text-white/80" : "text-gray-500"}`}>
                    {option.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ─── Business Model — tells the AI what business these calls belong to ─── */}
      <div className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <HiOutlineOfficeBuilding className="w-5 h-5 text-primary-600" />
            Business Model
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Select your business type. The AI uses it to identify{' '}
            <strong>what kind of call</strong> each recording is (sales, renewal, collection,
            support…) and to <strong>score the agent</strong> against your business's own playbook —
            e.g. insurance calls are judged on policy explanation and disclosure, real-estate calls
            on site-visit closing, banking calls on accurate rates and compliance.
          </p>
        </div>

        {businessModelOptions.length === 0 ? (
          <Card className="p-4">
            <p className="text-sm text-gray-500">
              Business model options could not be loaded. Please refresh the page.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {businessModelOptions.map((option) => {
              const Icon = option.icon;
              const isActive = businessModel === option.key;

              return (
                <button
                  key={option.key}
                  onClick={() => handleSelectBusinessModel(option.key)}
                  disabled={businessModelMutation.isPending}
                  title={option.description}
                  className={`
                    text-left p-4 rounded-xl border-2 transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${isActive
                      ? "border-primary-600 bg-primary-50 shadow-md"
                      : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"}
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isActive ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`font-semibold text-sm ${isActive ? "text-primary-700" : "text-gray-900"}`}>
                          {option.label}
                        </p>
                        {option.custom && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded">
                            Custom
                          </span>
                        )}
                        {isActive && (
                          <HiOutlineCheckCircle className="w-4 h-4 text-primary-600 flex-shrink-0" />
                        )}
                        {option.custom && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustom(option);
                            }}
                            disabled={deleteCustomMutation.isPending}
                            title="Remove this custom business model"
                            className="ml-auto p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                          >
                            <HiOutlineTrash className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{option.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Add Custom business model */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setShowCustomForm((v) => !v);
              setCustomFormError("");
            }}
            disabled={createCustomMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <HiOutlinePlus className="w-4 h-4" />
            {showCustomForm ? "Close form" : "Add Custom Business Model"}
          </button>
          <span className="text-xs text-gray-400">
            Not in the list? Define your own business type and tell the AI how to score your calls.
          </span>
        </div>

        {showCustomForm && (
          <Card className="p-4 sm:p-5 border-primary-200">
            <form onSubmit={handleCreateCustom} className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <HiOutlinePlusCircle className="w-5 h-5 text-primary-600" />
                  New Custom Business Model
                </h4>
                <p className="text-xs text-gray-500 mt-1">
                  The AI will use this to identify your call types and judge the agent against your
                  business's own playbook.
                </p>
              </div>

              {customFormError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-300">
                  <p className="text-sm text-red-700 text-center font-medium">{customFormError}</p>
                </div>
              )}

              <Input
                label="Business Model Name *"
                type="text"
                value={customForm.label}
                onChange={(e) => {
                  setCustomForm((f) => ({ ...f, label: e.target.value }));
                  setCustomFormError("");
                }}
                placeholder="e.g. Education Consultancy, Solar Panels, Pest Control"
                icon={<HiOutlineOfficeBuilding className="w-5 h-5" />}
                disabled={createCustomMutation.isPending}
                maxLength={60}
              />

              <Input
                label="Short Description"
                type="text"
                value={customForm.description}
                onChange={(e) => setCustomForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="One line: what this business sells / supports"
                icon={<HiOutlineBriefcase className="w-5 h-5" />}
                disabled={createCustomMutation.isPending}
                maxLength={200}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  How should the AI identify &amp; score these calls?
                </label>
                <textarea
                  value={customForm.guidance}
                  onChange={(e) => setCustomForm((f) => ({ ...f, guidance: e.target.value }))}
                  disabled={createCustomMutation.isPending}
                  maxLength={2000}
                  rows={5}
                  placeholder={"Describe your call types and what good performance looks like, e.g.\n\n• Call types: product enquiry, quotation follow-up, installation scheduling\n• Score high when: the agent qualifies the customer's requirement, explains pricing clearly, and books a site survey\n• Score low when: the agent gives wrong prices or pressures the customer"}
                  className="w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 resize-y"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Optional but recommended — a detailed playbook makes the AI scoring much more
                  accurate. Up to 2000 characters.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={createCustomMutation.isPending}
                  disabled={createCustomMutation.isPending}
                >
                  {createCustomMutation.isPending ? "Adding..." : "Add Business Model"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => {
                    setShowCustomForm(false);
                    setCustomFormError("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <HiOutlineSparkles className="w-3.5 h-3.5 flex-shrink-0" />
          New analyses use this business context immediately — existing analyses keep their
          original scoring. Use "Recalculate Marks" on an analysis to re-score it with the new
          business model.
        </p>
      </div>

      {/* ─── Transcription (Deepgram) API Key — AI Call Analysis ─── */}
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              keyStatus.configured ? "bg-green-50" : "bg-red-50"
            }`}
          >
            <HiOutlineKey
              className={`w-5 h-5 ${keyStatus.configured ? "text-green-600" : "text-red-600"}`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-gray-900">Transcription API Key</p>
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  keyStatus.configured
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {keyStatus.configured ? "Active" : apiKeyQuery.isLoading ? "Checking…" : "Not configured"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              This key powers AI Call Analysis (the speech-to-text transcription of your call
              recordings). When it expires, add a new key here and Call Analysis keeps working with
              it immediately — no restart and no error.
            </p>

            {canManageKey ? (
              <>
                {(keyStatus.masked || keyStatus.updatedAt) && (
                  <p className="text-[11px] text-gray-400 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {keyStatus.masked && (
                      <span>
                        Current key: <span className="font-mono">{keyStatus.masked}</span>
                        {keyStatus.source === "env" && " (server default)"}
                      </span>
                    )}
                    {keyStatus.updatedAt && keyStatus.source === "custom" && (
                      <span>
                        Updated: {new Date(keyStatus.updatedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    )}
                  </p>
                )}

                <form onSubmit={handleUpdateApiKey} className="mt-3 space-y-3">
                  <div className="relative">
                    <Input
                      label="New API Key"
                      type={showApiKey ? "text" : "password"}
                      value={apiKeyInput}
                      onChange={(e) => setApiKeyInput(e.target.value)}
                      placeholder="Paste the new Deepgram API key"
                      icon={<HiOutlineKey className="w-5 h-5" />}
                      disabled={updateApiKeyMutation.isPending}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey((v) => !v)}
                      title={showApiKey ? "Hide key" : "Show key"}
                      className="absolute right-3 bottom-2.5 p-1 rounded text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showApiKey ? <HiOutlineEyeOff className="w-4 h-4" /> : <HiOutlineEye className="w-4 h-4" />}
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      loading={updateApiKeyMutation.isPending}
                      disabled={updateApiKeyMutation.isPending || !apiKeyInput.trim()}
                    >
                      {updateApiKeyMutation.isPending ? "Validating & saving…" : "Update API Key"}
                    </Button>
                    {keyStatus.source === "custom" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="md"
                        disabled={clearApiKeyMutation.isPending || updateApiKeyMutation.isPending}
                        onClick={() => {
                          if (window.confirm("Remove the current API key? The server default will be restored.")) {
                            clearApiKeyMutation.mutate();
                          }
                        }}
                      >
                        {clearApiKeyMutation.isPending ? "Removing…" : "Remove Key"}
                      </Button>
                    )}
                  </div>

                  <p className="text-[11px] text-gray-400 flex items-start gap-1.5">
                    <HiOutlineExclamationCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    The new key is validated against Deepgram before it is saved — an invalid or
                    expired key is rejected, so Call Analysis never breaks. Get a key from the
                    Deepgram Console (deepgram.com).
                  </p>
                </form>
              </>
            ) : (
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <HiOutlineLockClosed className="w-3.5 h-3.5" />
                The transcription API key is managed by your organization's admins. Contact an admin
                if call analysis stops working because the key expired.
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Daily limit info (limit itself is superadmin-controlled) */}
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineSparkles className="w-5 h-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              Direct Analysis Limit — {limit} calls per day
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              In Direct mode, up to <strong>{limit}</strong> calls are analyzed automatically each day.
              Today's usage: <strong>{usedToday}</strong> of <strong>{limit}</strong>
              {mode === "manual" && " (limit applies only when Direct mode is active)"}.
            </p>
            {mode === "auto" && limit > 0 && (
              <div className="mt-2">
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${usedToday >= limit ? "bg-red-500" : "bg-purple-500"}`}
                    style={{ width: `${Math.min(100, (usedToday / limit) * 100)}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  {usedToday >= limit
                    ? "Daily limit reached — new recordings will not be analyzed until tomorrow."
                    : `${limit - usedToday} direct analyses remaining today`}
                </p>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <HiOutlineLockClosed className="w-3.5 h-3.5" />
              The daily limit is managed by your provider (Superadmin). Contact support to change it.
            </p>
          </div>
        </div>
      </Card>

      {/* Info Card */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <HiOutlineSparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">How the two modes work</p>
            <ul className="text-xs text-blue-700 mt-1.5 space-y-1">
              <li>
                • <strong>Manual</strong> — You control exactly which calls get analyzed. Open Call
                Analysis, press "Call Analysis" on any recording. No daily limit.
              </li>
              <li>
                • <strong>Direct (Automatic)</strong> — Recordings are analyzed automatically the
                moment they arrive (from calls or uploads), up to the daily limit. Beyond the limit,
                recordings still arrive — just switch any of them to analysis manually.
              </li>
              <li>
                • You can switch modes at any time; the change takes effect immediately for new
                recordings.
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CallSettings;